<template>
  <div class="">
    <svg :viewBox="viewBox">
      <g v-for="(value, index) in data" :key="index">
        <circle
          :cx="cx"
          :cy="cy"
          :r="radius"
          fill="transparent"
          :stroke="colors[index]"
          :stroke-width="width"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="calculateStrokeDashOffset(value, circumference)"
          :transform="returnCircleTransformValue(index)"
        />
        <defs>
          <filter id="rounded-corners" x="-30%" width="150%" y="-80%" height="250%">
            <feFlood flood-color="#000000" />
            <feGaussianBlur stdDeviation="2" />
            <feComponentTransfer>
              <feFuncA type="table" tableValues="0 0 0 1" />
            </feComponentTransfer>
            <feComponentTransfer>
              <feFuncA type="table" tableValues="0 1 1 1 1 1 1 1" />
            </feComponentTransfer>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.6" />
            </feComponentTransfer>
            <feComposite operator="over" in="SourceGraphic" />
          </filter>

          <filter id="rounded-corners-2" primitiveUnits="objectBoundingBox">
            <feImage
              preserveAspectRatio="none"
              width="110%"
              height="110%"
              x="-5%"
              y="0%"
              xlink:href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' viewBox='0 0 400 40' height='40' width='400'%3E%3Crect fill='red' x='0' y='0' rx='10' ry='10' width='400' height='40'/%3E%3C/svg%3E"
            />
            <feComposite operator="over" in="SourceGraphic" />
          </filter>
        </defs>
        <text
          class="category-percentage"
          filter="url(#rounded-corners)"
          v-if="segmentBigEnough(value)"
          text-anchor="middle"
          dy="3px"
          :x="chartData[index].textX"
          :y="chartData[index].textY"
          fill="#ffffff"
        >
          {{ processPercentage(value) }} %
        </text>
      </g>
      <g>
        <text class="percentage-number" :x="cx" :y="cy - 10" dominant-baseline="middle" text-anchor="middle">
          {{ effectiveness }}%
        </text>
        <text class="percentage-text" :x="cx" :y="cy + 12" dominant-baseline="middle" text-anchor="middle">
          {{ $t('passes_possession.passes_effectiveness.first') }}
        </text>
        <text class="percentage-text" :x="cx" :y="cy + 24" dominant-baseline="middle" text-anchor="middle">
          {{ $t('passes_possession.passes_effectiveness.second') }}
        </text>
      </g>
      <g v-if="showLabels">
        <g
          v-for="(category, index) in categories"
          :key="`cat-${index}`"
          class="item-play-hover"
          @click="setOpenMedia(category, effectiveness)"
        >
          <rect :x="180 - difX" :y="80 + difY + index * 24" width="112" height="16" :fill="colors[index]" rx="4" />
          <text
            :x="index == 2 ? 229 - difX : index == 1 ? 213 - difX : 220 - difX"
            :y="80 + difY + index * 24 + 9"
            class="category-text"
            :fill="backColors[index]"
            dominant-baseline="middle"
            text-anchor="middle"
          >
            {{ $t(category.translationKey) }}
          </text>
          <image
            :class="effectiveness == 0 ? 'disabled-icon' : ''"
            :x="242 - difX"
            :y="viewBoxIsMobile ? 83 + index * 24 : 163 + index * 24"
            width="80"
            height="10"
            :href="
              index == 0
                ? 'https://golstatsimages.blob.core.windows.net/todoelfut/icn_playsvg.svg'
                : 'https://golstatsimages.blob.core.windows.net/todoelfut/icn_play_gray-1.svg'
            "
          />
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
export default {
  name: 'PassesCircleBar',
  props: {
    data: {
      type: Array,
      required: true,
    },
    colors: {
      type: Array,
      required: false,
      default: function() {
        return ['#9013fe', '#f8e71c', '#e3e3e4'];
      },
    },
    backColors: {
      type: Array,
      required: false,
      default: function() {
        return ['#ffffff', '#48515a', '#48515a'];
      },
    },
    categories: {
      type: Array,
      required: false,
      default: function() {
        return [
          {
            translationKey: 'passes_possession.own_half',
            generalName: 'Pases cancha propia',
            singleName: 'Pase cancha propia',
          },
          {
            translationKey: 'passes_possession.opponents_half',
            generalName: 'Pases cancha rival',
            singleName: 'Pase cancha rival',
          },
          {
            translationKey: 'passes_possession.unsuccessful_passes',
            generalName: 'Pases no acertados',
            singleName: 'Pase no acertado',
          },
        ];
      },
    },
    startAngle: {
      type: Number,
      required: false,
      default: -90,
    },
    width: {
      type: Number,
      required: false,
      default: 20,
    },
    showLabels: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      cx: 80,
      cy: 80,
      radius: 60,
    };
  },
  computed: {
    ...mapState(['windowWidth']),
    ...mapGetters('filterGeneral', {
      getGamesPlayedByTeam: 'getGamesPlayedByTeam',
      getResults: 'getResults',
      getSections: 'getSections',
      getPlayingAs: 'getPlayingAs',
      getSelectedGamesIds: 'getSelectedGamesIds',
      seasonSelected: 'getSeasonId',
    }),
    ...mapGetters('teamsGeneral', {
      teamId: 'teamId',
    }),
    ...mapState('filterGeneral', ['tournamentsForMediaPlayer']),
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    dataTotal() {
      return this.data.reduce((acc, val) => acc + val, 0);
    },
    effectiveness() {
      return Math.round(((this.data[0] + this.data[1]) * 100) / this.dataTotal) || 0;
    },
    chartData() {
      let angleOffset = this.startAngle;
      return this.data.reduce((a, v) => {
        const { x, y } = this.calculateTextCoords(v, angleOffset);
        a.push({
          degrees: angleOffset,
          textX: x,
          textY: y,
        });
        angleOffset = (v / this.dataTotal) * 360 + angleOffset;
        return a;
      }, []);
    },
    viewBox() {
      let dimension =
        this.windowWidth >= 1024 ? (this.windowWidth >= 1500 ? '-40 0 400 160' : '0 0 300 160') : '-20 0 200 230';
      return this.showLabels ? dimension : '0 0 160 160';
    },
    viewBoxIsMobile() {
      return this.windowWidth >= 1024 ? true : false;
    },
    difX() {
      return this.windowWidth >= 1024 ? 0 : 155;
    },
    difY() {
      return this.windowWidth >= 1024 ? 0 : 80;
    },
  },
  methods: {
    ...mapMutations('mediaModal', ['setModalInitialState', 'setIsModalOpen']),
    setOpenMedia(category, value) {
      if (value != 0) {
        let categories = [];
        switch (category.translationKey) {
          case 'passes_possession.own_half':
            categories = [2];
            break;
          case 'passes_possession.unsuccessful_passes':
            categories = [3, 21];
            break;
          case 'passes_possession.opponents_half':
            categories = [20];
            break;
        }
        let jsonConfig = {
          seasonId: this.seasonSelected,
          tournaments: this.tournamentsForMediaPlayer,
          categoryIds: categories,
          categorySingleName: category.singleName,
          mainSectionType: 1,
          playListType: 3,
          gameIds: this.getSelectedGamesIds,
          teamId: this.teamId,
          playerIds: [],
          matchResult: this.getResults,
          playData: {},
          advancedFilter: {
            playListTitle: category.generalName,
            mainCategoryId: {},
            playingAs: this.getPlayingAs ? [this.getPlayingAs] : [1, 2],
            timeSections: this.getSections,
            fieldSections: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
          },
        };
        this.setModalInitialState(jsonConfig);
        this.setIsModalOpen(true);
      }
    },
    calculateStrokeDashOffset(dataVal, circumference) {
      const strokeDiff = (dataVal / this.dataTotal) * circumference;
      return circumference - strokeDiff;
    },
    returnCircleTransformValue(index) {
      return `rotate(${this.chartData[index].degrees || 0}, ${this.cx}, ${this.cy})`;
    },
    degreesToRadians(angle) {
      return angle * (Math.PI / 180);
    },
    calculateTextCoords(dataVal, angleOffset) {
      const angle = ((dataVal / this.dataTotal) * 360) / 2 + angleOffset;
      const radians = this.degreesToRadians(angle);

      return {
        x: this.radius * Math.cos(radians) + this.cx,
        y: this.radius * Math.sin(radians) + this.cy,
      };
    },
    percentageLabel(dataVal) {
      return `${Math.round((dataVal / this.dataTotal) * 100)}%`;
    },
    segmentBigEnough(dataVal) {
      return Math.round(this.dataPercentage(dataVal) * 100) > 5;
    },
    dataPercentage(dataVal) {
      return dataVal / this.dataTotal;
    },
    processPercentage(value) {
      return ((100 * value) / this.dataTotal).toFixed(2);
    },
  },
};
</script>

<style scoped>
.disabled-icon {
  opacity: 0.6;
}
.item-play-hover:hover {
  opacity: 0.8;
  cursor: pointer;
}
.class-invert {
  filter: invert(0.7);
}
.percentage-number {
  font-size: 22px;
  fill: #808285;
}

.percentage-text {
  font-size: 10px;
  fill: #808285;
}

.category-text {
  font-size: 8px;
  font-family: 'Avenir-Demi';
}

.category-percentage {
  font-weight: bold;
  font-size: 6.5px;
}
</style>
